const getFormatProps = (
  formatProps: FormatProps,
  format: string
): Record<string, string | boolean | undefined> => {
  return Object.entries(formatProps).reduce((acc, [key, value]) => {
    acc[`${format}.${key}`] = value;
    return acc;
  }, {} as Record<string, string | boolean | undefined>);
};

const getLocationProps = (
  geometry: GeometryFields,
  format: string
): Record<string, string | boolean | undefined> => {
  switch (geometry.locationType) {
    case '__SINGLE__':
      return {
        [`${format}.geometryField`]: geometry.geometryField,
        [`${format}.geometryFieldFormat`]: geometry.geometryFieldFormat,
        [`${format}.buildGeometryFromFields`]: false
      };
    case '__XY__':
      return {
        [`${format}.xField`]: geometry.xField,
        [`${format}.yField`]: geometry.yField,
        [`${format}.zField`]: geometry.zField,
        [`${format}.geometryFieldFormat`]: geometry.geometryFieldFormat,
        [`${format}.buildGeometryFromFields`]: true
      };
    default:
      return {};
  }
};

const getDateTimeProps = (
  dateTime: DateTimeFields,
  format: string
): Record<string, string | undefined> => {
  if (!dateTime.hasDateTime) return {};
  return {
    [`${format}.dateFormattingString`]: dateTime.dateFormattingString
  };
};

const getSchemaGeometry = (geometry: GeometryFields): InputSchemaGeometry => {
  if (
    geometry.locationType !== '__NONE__' &&
    geometry.geometryType !== '__NONE__'
  ) {
    return geometry.spatialReference?.wkid
      ? {
          geometryType: geometry.geometryType,
          spatialReference: { wkid: geometry.spatialReference?.wkid }
        }
      : {
          geometryType: geometry.geometryType
        };
  }
  return {};
};

const getSchemaTime = (dateTime: DateTimeFields): InputSchemaTime => {
  // If data has no date/time or start time is not defined
  if (
    !dateTime.hasDateTime ||
    !dateTime.startTimeField ||
    dateTime.startTimeField === '__NONE__'
  )
    return {};
  // If data has date/time AND start is defined but there is no endtime defined
  if (!dateTime.endTimeField || dateTime.endTimeField === '__NONE__')
    return { timeType: 'Instant' };
  return { timeType: 'Interval' };
};

const getFieldMappings = (schemaFields: SchemaFields): FieldMapping[] => {
  return Object.entries(schemaFields)
    .filter(([, { mapped }]) => mapped)
    .map(([fromField, { toName }]) => {
      return {
        fromField,
        toField: toName,
        tags: []
      };
    });
};

const getInputSchema = (
  schemaFields: SchemaFields,
  keyFields: KeyFields
): InputSchema => {
  return {
    attributes: Object.entries(schemaFields).map(([name, { dataType }]) => {
      return {
        name,
        dataType,
        nullable: true,
        tags: []
      };
    }),
    geometry: getSchemaGeometry(keyFields.geometry),
    time: getSchemaTime(keyFields.dateTime)
  };
};

const addTrackFieldTag = (
  inputAttributes: Attribute[],
  fieldMappings: FieldMapping[],
  trackingField: string | undefined
): void => {
  const attributes = inputAttributes?.find(
    ({ name }) => name === trackingField
  );
  if (attributes) attributes.tags.push('TRACK_ID');
  const mapping = fieldMappings.find(
    ({ fromField }) => fromField === trackingField
  );
  if (mapping) mapping.tags.push('TRACK_ID');
};

const addTimeFieldTag = (
  inputAttributes: Attribute[],
  fieldMappings: FieldMapping[],
  timeField: string,
  tag: Tag
) => {
  const timeAttribute = inputAttributes?.find(({ name }) => name === timeField);
  if (timeAttribute) {
    timeAttribute.dataType = 'Date';
    timeAttribute.tags.push(tag);
  }
  const startTimeMapping = fieldMappings.find(
    ({ fromField }) => fromField === timeField
  );
  if (startTimeMapping) startTimeMapping.tags.push(tag);
};

const addStartEndFieldTags = (
  inputAttributes: Attribute[],
  fieldMappings: FieldMapping[],
  { startTimeField, endTimeField }: DateTimeFields
): void => {
  if (startTimeField && startTimeField !== '__NONE__') {
    addTimeFieldTag(
      inputAttributes,
      fieldMappings,
      startTimeField,
      'START_TIME'
    );
  }
  if (endTimeField && endTimeField !== '__NONE__') {
    addTimeFieldTag(inputAttributes, fieldMappings, endTimeField, 'END_TIME');
  }
};

export const getCalculateSizePayload = (
  schemaFields: SchemaFields,
  formatName: FormatName,
  formatProps: FormatProps,
  keyFields: KeyFields,
  content: string
): CalculateSizePayload => {
  const inputSchema = getInputSchema(schemaFields, keyFields);
  const fieldMappings = getFieldMappings(schemaFields);
  addTrackFieldTag(
    inputSchema.attributes,
    fieldMappings,
    keyFields.trackingField
  );
  addStartEndFieldTags(
    inputSchema.attributes,
    fieldMappings,
    keyFields.dateTime
  );
  return {
    content,
    formatName,
    properties: {
      ...getFormatProps(formatProps, formatName),
      ...getLocationProps(keyFields.geometry, formatName),
      ...getDateTimeProps(keyFields.dateTime, formatName)
    },
    schemaTransformation: {
      fieldMappings,
      inputSchema
    }
  };
};

export const getSchemaRequestPayload = (
  content: string,
  formatName: FormatName,
  formatProps: FormatProps
): DerivePayload => {
  return {
    content,
    formatName,
    properties: getFormatProps(formatProps, formatName)
  };
};
